import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Save, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';
import { recalculateAdminPaymentFields } from '../../utils/orderSave';
import { parseSignedNumberInput, toFiniteNumber } from '../../utils/numbers';
import AdminRoute from './AdminRoute';

const emptyPayment = () => ({
  Id: 0,
  VoucherNo: '',
  ProductPrice: 0,
  DeliveryCharge: 0,
  TAX: 0,
  ClientAmount: 0,
  CustomerAmount: 0,
  ChargeFrom: 'CLIENT',
  PayType: 'CAD',
  TempServiceCharge: 0,
  delchargechanged: false,
  ClientName: '',
  CustomerName: '',
  StatusName: '',
});

const AdminOrderPaymentEdit = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const [voucherNo, setVoucherNo] = useState('');
  const [payment, setPayment] = useState(emptyPayment);
  const [companyProfile, setCompanyProfile] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    MainService.GetCompanyProfile()
      .then((profile) => setCompanyProfile(profile || {}))
      .catch((err) => console.error('Error loading company profile:', err));
  }, []);

  useEffect(() => {
    if (!loaded || !payment.Id || Object.keys(companyProfile).length === 0) return;
    setPayment((prev) => recalculateAdminPaymentFields(prev, {}, companyProfile));
  }, [companyProfile, loaded, payment.Id]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  const resetPage = ({ clearMessages = true } = {}) => {
    setVoucherNo('');
    setPayment(emptyPayment());
    setLoaded(false);
    if (clearMessages) {
      setError('');
      setSuccess('');
    }
  };

  const updatePaymentField = useCallback(
    (field, value) => {
      setPayment((prev) => recalculateAdminPaymentFields(prev, { [field]: value }, companyProfile));
    },
    [companyProfile]
  );

  const loadOrder = async (voucher) => {
    const value = String(voucher ?? voucherNo ?? '').trim();
    if (!value) {
      setError('Enter Voucher Number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.OrderByVoucherNo(value);
      if (!response?.Id) {
        setError('Order not found');
        return;
      }

      const initial = {
        Id: response.Id,
        VoucherNo: response.VoucherNo || value,
        ProductPrice: toFiniteNumber(response.ProductPrice),
        DeliveryCharge: toFiniteNumber(response.DeliveryCharge),
        TAX: toFiniteNumber(response.TAX),
        ClientAmount: toFiniteNumber(response.ClientAmount),
        CustomerAmount: toFiniteNumber(response.CustomerAmount),
        ChargeFrom: response.ChargeFrom || 'CLIENT',
        PayType: response.PayType || 'CAD',
        TempServiceCharge: toFiniteNumber(response.TempServiceCharge ?? response.DeliveryCharge),
        delchargechanged: false,
        ClientName: response.Client?.Name || '',
        CustomerName: response.Customer?.Name || '',
        StatusName: response.Status?.Name || '',
      };
      setPayment(recalculateAdminPaymentFields(emptyPayment(), initial, companyProfile));
      setVoucherNo(response.VoucherNo || value);
      setLoaded(true);
    } catch (err) {
      console.error('Error loading order by voucher:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const savePayment = async () => {
    if (!payment.Id) {
      setError('Enter Voucher Number');
      return;
    }

    const calculated = recalculateAdminPaymentFields(payment, {}, companyProfile);

    try {
      setLoading(true);
      setError('');
      const payload = {
        Id: calculated.Id,
        ProductPrice: toFiniteNumber(calculated.ProductPrice),
        DeliveryCharge: toFiniteNumber(calculated.DeliveryCharge),
        TAX: toFiniteNumber(calculated.TAX),
        ClientAmount: toFiniteNumber(calculated.ClientAmount),
        CustomerAmount: toFiniteNumber(calculated.CustomerAmount),
        ChargeFrom: calculated.ChargeFrom,
        PayType: calculated.PayType,
      };
      const response = await MainService.UpdateDateOrderPayment(payload);
      if (response === true) {
        setSuccess('Success');
        setTimeout(() => resetPage({ clearMessages: false }), 1000);
      } else {
        setError('Failed');
      }
    } catch (err) {
      console.error('Error updating order payment:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';
  const disabledClass = `${inputClass} disabled:bg-gray-50 disabled:text-gray-600`;

  return (
    <AdminRoute>
      <div className="space-y-4 pb-28">
        {error && (
          <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button type="button" onClick={() => setSuccess('')} className="flex-shrink-0 text-green-500 hover:text-green-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="mb-1 text-2xl font-bold text-gray-900">{getPageHeading(language, 'adminOrderPaymentEdit')}</h1>
          <p className="mb-4 text-sm text-gray-500">Enter voucher number to update payment fields.</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] max-w-sm flex-1">
              <label className="mb-1 block text-sm font-medium text-gray-700">{ui('orders.createOrder.voucherNo')}</label>
              <input
                type="text"
                className={disabledClass}
                value={voucherNo}
                onChange={(e) => setVoucherNo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loaded) {
                    e.preventDefault();
                    loadOrder(voucherNo);
                  }
                }}
                disabled={loaded}
                placeholder={ui('orders.createOrder.voucherNo')}
              />
            </div>
            {!loaded && (
              <button
                type="button"
                onClick={() => loadOrder(voucherNo)}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                <Search className="h-4 w-4" />
                {ui('common.search')}
              </button>
            )}
            {loaded && payment.StatusName && (
              <p className="pb-2 text-sm text-gray-600">
                {ui('orders.createOrder.statusPrefix')} {payment.StatusName}
              </p>
            )}
          </div>
        </div>

        {loaded && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">{ui('orders.createOrder.orderDetailsFinancial')}</h2>

            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.clientName')}</label>
                <input type="text" className={disabledClass} value={payment.ClientName} disabled readOnly />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.customerName')}</label>
                <input type="text" className={disabledClass} value={payment.CustomerName} disabled readOnly />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.payType')}</label>
                <select
                  className={inputClass}
                  value={payment.PayType}
                  onChange={(e) => updatePaymentField('PayType', e.target.value)}
                >
                  <option value="CAD">CAD</option>
                  <option value="COD">COD</option>
                  <option value="CAO">CAO</option>
                  <option value="TAO">TAO</option>
                  <option value="FOC">FOC</option>
                  <option value="APS">APS</option>
                  <option value="APR">APR</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.chargeFrom')}</label>
                <select
                  className={inputClass}
                  value={payment.ChargeFrom}
                  onChange={(e) => updatePaymentField('ChargeFrom', e.target.value)}
                >
                  <option value="CLIENT">{ui('orders.createOrder.chargeFromClient')}</option>
                  <option value="AREA">{ui('orders.createOrder.chargeFromArea')}</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.productPrice')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className={inputClass}
                  value={payment.ProductPrice}
                  onChange={(e) => {
                    const value = parseSignedNumberInput(e.target.value);
                    if (value === null) return;
                    updatePaymentField('ProductPrice', value);
                  }}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.deliveryCharge')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className={inputClass}
                  value={payment.DeliveryCharge}
                  onChange={(e) => {
                    const value = parseSignedNumberInput(e.target.value);
                    if (value === null) return;
                    updatePaymentField('DeliveryCharge', value);
                  }}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {ui('orders.createOrder.tax')}
                  {companyProfile.DefTAX ? ` (${companyProfile.DefTAX}%)` : ''}
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700"
                  value={payment.TAX}
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.clientAmount')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full rounded-md border border-gray-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-900"
                  value={payment.ClientAmount}
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.customerAmount')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full rounded-md border border-gray-300 bg-green-50 px-3 py-2 text-sm font-semibold text-green-900"
                  value={payment.CustomerAmount}
                  disabled
                  readOnly
                />
              </div>
            </div>
          </div>
        )}

        {loaded && (
          <div className="fixed bottom-8 left-1/2 z-40 flex -translate-x-1/2 items-center justify-center gap-3 rounded-2xl border-2 border-gray-200 bg-white px-6 py-4 shadow-xl">
            {loading ? (
              <RefreshCw className="h-6 w-6 animate-spin text-primary" />
            ) : (
              <>
                <button
                  type="button"
                  onClick={savePayment}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-white shadow-sm transition-colors hover:bg-emerald-700"
                >
                  <Save className="h-4 w-4" />
                  {ui('common.save')}
                </button>
                <button
                  type="button"
                  onClick={() => resetPage()}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-white shadow-sm transition-colors hover:bg-amber-600"
                >
                  <RefreshCw className="h-4 w-4" />
                  {ui('common.clear')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </AdminRoute>
  );
};

export default AdminOrderPaymentEdit;
